import { ReactNode, useState } from 'react';

import { InviteStatus } from '@ndvr/auth-api';
import { PersonOrgProfile } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { formatDateTime, parseServerDateTime } from 'utils/date-formatter';
import { usePersonOrgLastLoginsForHousehold } from 'views/household/household-queries';
import { useInviteDetails, useInvitePerson } from 'views/person/person-queries';
import { ConfirmModal, ModalText } from 'widgets';

import { accountTitle } from './account-model';
import {
    AutosaveNote,
    CardFooter,
    CollapsibleCard,
    linkButtonClass,
    needsAttention,
    Notice,
    NotSavedMark,
    StatusText,
} from './fields';
import { completion, joinNames, PersonKind } from './onboarding-model';
import { useRemoveHouseholdPerson } from './onboarding-tab-queries';
import { Autosave } from './useAutosave';
import { OnboardingHousehold } from './useOnboardingHousehold';

export interface PersonCardProps {
    householdGUID: string;
    household: OnboardingHousehold;
    profile: PersonOrgProfile;
    open: boolean;
    onToggle: () => void;
    /** Opens Add person; `onCreated` gets the new person, e.g. to pick them as a trusted contact. */
    addPerson: (kind: PersonKind, onCreated: (personOrgGUID: string) => void) => void;
}

/**
 * The collapsible card every person and entity shares: name, Client Portal state and completion in the header; how
 * saving stands and Remove under the body. Remove is refused while the person owns an account or is a beneficiary on
 * one, and asks for confirmation otherwise.
 */
export const PersonCardShell = ({
    householdGUID,
    household,
    profile,
    kind,
    open,
    onToggle,
    missing,
    autosave,
    children,
}: Omit<PersonCardProps, 'addPerson'> & {
    kind: PersonKind;
    /** Required fields still empty; `undefined` shows no status (trusted professionals). */
    missing?: number;
    autosave: Autosave;
    children: ReactNode;
}) => {
    const [blocked, setBlocked] = useState('');
    const [confirmRemove, setConfirmRemove] = useState(false);
    const remove = useRemoveHouseholdPerson(householdGUID);
    const name = household.nameOf(profile.personOrgGUID);
    const attention = needsAttention(autosave);

    const tryRemove = () => {
        const { owner, beneficiary } = household.involvement(profile.personOrgGUID);
        const roles = [
            ...owner.map((row) => `an owner on ${accountTitle(row.account)}`),
            ...beneficiary.map((row) => `a beneficiary on ${accountTitle(row.account)}`),
        ];
        if (roles.length) {
            setBlocked(
                `${name} can’t be removed. They’re ${joinNames(roles)}. Remove them from ${
                    roles.length === 1 ? 'that account' : 'those accounts'
                } first.`,
            );
            return;
        }
        setConfirmRemove(true);
    };

    const status = missing === undefined ? undefined : completion(missing);
    return (
        <CollapsibleCard
            id={`person-${profile.personOrgGUID}`}
            open={open}
            bodyProps={autosave.bodyProps}
            onToggle={() => {
                setBlocked('');
                onToggle();
            }}
            header={
                <>
                    <span className="font-medium whitespace-nowrap">{name}</span>
                    {kind === PersonKind.CLIENT && <PortalState profile={profile} householdGUID={householdGUID} />}
                    <span className="flex-1" />
                    {kind !== PersonKind.ENTITY && kind !== PersonKind.CLIENT && <InviteButton profile={profile} />}
                    {attention && <NotSavedMark />}
                    {status && <StatusText {...status} />}
                </>
            }
        >
            {children}
            <CardFooter floating={attention}>
                <AutosaveNote autosave={autosave} />
                <span className="flex-1" />
                <button
                    type="button"
                    onClick={tryRemove}
                    disabled={remove.isPending}
                    className="text-cocoa-600 cursor-pointer text-[13px] underline underline-offset-[3px]"
                >
                    Remove
                </button>
            </CardFooter>
            {blocked && (
                <div className="mb-4">
                    <Notice>{blocked}</Notice>
                </div>
            )}
            {confirmRemove && (
                <ConfirmModal
                    modalTitle={`Remove ${name}`}
                    onConfirm={() => remove.mutate(profile.personOrgGUID)}
                    buttonProps={{ buttonType: 'destructive', children: 'Remove' }}
                    onClose={() => setConfirmRemove(false)}
                >
                    <ModalText>{name} is removed from the household. Their record is kept.</ModalText>
                </ConfirmModal>
            )}
        </CollapsibleCard>
    );
};

/** "Registered · last login Oct 16, 2026 09:12 AM", "Invited Oct 14, 2026 03:42 PM · Resend" or "Not invited · Invite". */
const PortalState = ({ profile, householdGUID }: { profile: PersonOrgProfile; householdGUID: string }) => {
    const { data: invite } = useInviteDetails(profile.personOrgGUID);
    const { data: lastLogins } = usePersonOrgLastLoginsForHousehold(householdGUID);
    const send = useInvitePerson(profile.personOrg.tenantGUID);
    const hasEmail = !!profile.personOrg.primaryEmail?.plainText;
    const lastLogin = lastLogins?.get(profile.personOrgGUID);
    if (!invite) return null;

    const action = (label: string) => (
        <>
            <span className="text-cocoa-400">·</span>
            <button
                type="button"
                className={linkButtonClass}
                disabled={!hasEmail || send.isPending}
                title={hasEmail ? undefined : 'Add an email to invite'}
                onClick={(event) => {
                    event.stopPropagation();
                    send.mutate(profile.personOrgGUID);
                }}
            >
                {send.isSuccess ? 'Sent' : label}
            </button>
        </>
    );

    return (
        <span className="text-cocoa-600 flex items-baseline gap-1.5 text-sm whitespace-nowrap">
            {invite.inviteStatus === InviteStatus.REGISTERED && (
                <>Registered · last login {formatDateTime(lastLogin && parseServerDateTime(lastLogin)) || 'never'}</>
            )}
            {(invite.inviteStatus === InviteStatus.INVITED || invite.inviteStatus === InviteStatus.INVITE_EXPIRED) && (
                <>
                    {invite.inviteStatus === InviteStatus.INVITE_EXPIRED ? 'Invite expired' : 'Invited'}{' '}
                    {formatDateTime(invite.latestInviteSentAt && parseServerDateTime(invite.latestInviteSentAt))}
                    {action('Resend')}
                </>
            )}
            {invite.inviteStatus === InviteStatus.NOT_INVITED && <>Not invited{action('Invite')}</>}
        </span>
    );
};

const InviteButton = ({ profile }: { profile: PersonOrgProfile }) => {
    const { data: invite } = useInviteDetails(profile.personOrgGUID);
    const send = useInvitePerson(profile.personOrg.tenantGUID);
    const hasEmail = !!profile.personOrg.primaryEmail?.plainText;
    const invited = !!invite && invite.inviteStatus !== InviteStatus.NOT_INVITED;
    return (
        <Button
            variant="outline"
            size="xs"
            disabled={!hasEmail || invited || send.isPending}
            title={hasEmail ? undefined : 'Add an email to invite'}
            onClick={(event) => {
                event.stopPropagation();
                send.mutate(profile.personOrgGUID);
            }}
        >
            {invited || send.isSuccess ? 'Invite sent' : 'Invite to Client Portal'}
        </Button>
    );
};
